import {
  ActionType,
  PLAYER_COLORS,
  type Entity as GameEntity,
  type OwnedComponent,
  type Tile as TileType,
} from '@sheepocalypse/shared';
import { useGameStore, useSocketStore } from '../../stores/socketStore';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { SHOP_TOOLTIP_BY_KEY } from '../ui/shopCatalog';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface TileProps {
  position: { x: number; y: number };
  tile?: TileType;
}

const TILE_ASSETS: Record<string, string> = {
  GRASS: '/assets/tiles/tile_grass.png',
};

const ENTITY_ASSETS = {
  sheep: [
    '/assets/entities/sheep_blue.png',
    '/assets/entities/sheep_green.png',
    '/assets/entities/sheep_purple.png',
    '/assets/entities/sheep_yellow.png',
  ],
  kingSheep: [
    '/assets/entities/sheep_blue_king.png',
    '/assets/entities/sheep_green_king.png',
    '/assets/entities/sheep_purple_king.png',
    '/assets/entities/sheep_yellow_king.png',
  ],
  dachshund: '/assets/entities/dachshund.png',
  goldenDachshund: '/assets/entities/dachshund_gold.png',
  shepherdDog: '/assets/entities/shepherd.png',
  blackSheep: '/assets/entities/black_sheep.png',
};

export function Tile({ position, tile }: TileProps) {
  const { gameState, validActions, placementFilter, cardTargetingCardId, setCardTargetingCardId } =
    useGameStore();
  const { submitAction } = useSocketStore();

  if (!gameState || !tile) return null;

  // Get entities on this tile
  const entities = tile.entities.map(id => gameState.entities[id]).filter(Boolean);

  // Check if this tile is a valid placement target
  const matchingTileActions = validActions.filter(action => {
    const supportsPosition =
      action.type === ActionType.PLACE_SHEEP ||
      action.type === ActionType.PLACE_KING_SHEEP ||
      action.type === ActionType.PLACE_DOG ||
      action.type === ActionType.MOVE_BLACK_SHEEP ||
      action.type === ActionType.PLAY_CARD;

    if (!supportsPosition) {
      return false;
    }

    if (!action.position) {
      return false;
    }

    return action.position.x === position.x && action.position.y === position.y;
  });

  const filteredTileAction = matchingTileActions.find(action => {
    if (cardTargetingCardId) {
      return action.type === ActionType.PLAY_CARD && action.cardId === cardTargetingCardId;
    }

    if (!placementFilter) {
      return action.type !== ActionType.PLACE_DOG;
    }

    if (placementFilter === 'PLACE_SHEEP') {
      return action.type === ActionType.PLACE_SHEEP;
    }

    if (placementFilter === 'PLACE_DOG_ANY') {
      return action.type === ActionType.PLACE_DOG;
    }

    return action.type === ActionType.PLACE_DOG && action.entityType === placementFilter;
  });

  const tileAction =
    filteredTileAction ??
    (!placementFilter && !cardTargetingCardId
      ? (matchingTileActions.find(action => action.type !== ActionType.PLACE_DOG) ??
        matchingTileActions[0])
      : undefined);

  const canPlaceSheep = tileAction?.type === ActionType.PLACE_SHEEP;
  const canPlaceKingSheep = tileAction?.type === ActionType.PLACE_KING_SHEEP;
  const canMoveBlackSheep = tileAction?.type === ActionType.MOVE_BLACK_SHEEP;
  const canTargetTile = Boolean(tileAction);

  const handleClick = () => {
    if (tileAction) {
      if (tileAction.type === ActionType.PLAY_CARD) {
        setCardTargetingCardId(null);
      }

      submitAction(tileAction);
    }
  };

  const tileStyle =
    tile.type in TILE_ASSETS
      ? {
          backgroundImage: `url(${TILE_ASSETS[tile.type]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : undefined;

  return (
    <div
      data-testid={`tile-${position.x}-${position.y}`}
      data-can-place-sheep={canPlaceSheep ? 'true' : 'false'}
      data-can-place-king-sheep={canPlaceKingSheep ? 'true' : 'false'}
      data-can-move-black-sheep={canMoveBlackSheep ? 'true' : 'false'}
      data-can-place-dog={tileAction?.type === ActionType.PLACE_DOG ? 'true' : 'false'}
      data-can-target-card={tileAction?.type === ActionType.PLAY_CARD ? 'true' : 'false'}
      onClick={handleClick}
      className={cn(
        'aspect-square rounded-lg relative overflow-hidden transition-all',
        !tileStyle && 'bg-transparent',
        canTargetTile && 'cursor-pointer ring-2 ring-blue-400 ring-offset-2',
        !tile.isPassable && 'cursor-not-allowed'
      )}
      style={tileStyle}
    >
      {/* Render entities */}
      <div className="absolute inset-0 flex items-center justify-center p-0.5">
        {entities.map(entity => (
          <EntityIcon key={entity.id} entity={entity} gameState={gameState} />
        ))}
      </div>

      {/* Black sheep indicator */}
      {gameState.blackSheepPosition &&
        gameState.blackSheepPosition.x === position.x &&
        gameState.blackSheepPosition.y === position.y && (
          <img
            className="absolute top-1/2 left-1/2 w-11/12 h-11/12 -translate-x-1/2 -translate-y-1/2 object-contain"
            src={ENTITY_ASSETS.blackSheep}
            alt="Black Sheep"
            title="Black Sheep: no owner, can be moved on roll 1, explodes on roll 6 tails."
          />
        )}
    </div>
  );
}

function EntityIcon({ entity, gameState }: { entity: GameEntity; gameState: any }) {
  const ownerComponent = entity.components.find(
    (component): component is OwnedComponent => component.type === 'OWNED'
  );
  const ownerId = ownerComponent?.ownerId;
  const ownerIndex = ownerId ? gameState.playerOrder.indexOf(ownerId) : -1;
  const useOutline = ownerIndex >= 4;

  switch (entity.type) {
    case 'KING_SHEEP':
      return (
        <img
          className={cn(
            'w-11/12 h-11/12 object-contain',
            useOutline && 'drop-shadow-[0_0_2px_rgba(255,255,255,0.95)]'
          )}
          src={getKingSheepAsset(ownerId, gameState)}
          alt="King Sheep"
          title={SHOP_TOOLTIP_BY_KEY.sheep}
        />
      );
    case 'SHEEP':
      return (
        <img
          className={cn(
            'w-11/12 h-11/12 object-contain',
            useOutline && 'drop-shadow-[0_0_2px_rgba(255,255,255,0.95)]'
          )}
          src={getSheepAsset(ownerId, gameState)}
          alt="Sheep"
          title={SHOP_TOOLTIP_BY_KEY.sheep}
        />
      );
    case 'DOG':
    case 'DACHSHUND':
      return (
        <img
          className="w-11/12 h-11/12 object-contain"
          src={ENTITY_ASSETS.dachshund}
          alt="Dachshund"
          title={SHOP_TOOLTIP_BY_KEY.dachshund}
        />
      );
    case 'SHEPHERD_DOG':
      return (
        <img
          className="w-11/12 h-11/12 object-contain"
          src={ENTITY_ASSETS.shepherdDog}
          alt="Shepherd Dog"
          title={SHOP_TOOLTIP_BY_KEY.shepherdDog}
        />
      );
    case 'GOLDEN_DACHSHUND':
      return (
        <img
          className="w-11/12 h-11/12 object-contain"
          src={ENTITY_ASSETS.goldenDachshund}
          alt="Golden Dachshund"
          title={SHOP_TOOLTIP_BY_KEY.goldenDachshund}
        />
      );
    default:
      return null;
  }
}

function getSheepAsset(ownerId: string | undefined, gameState: any): string {
  if (!ownerId) {
    return ENTITY_ASSETS.sheep[0];
  }

  const player = gameState.players[ownerId];
  if (player?.color) {
    const paletteIndex = PLAYER_COLORS.indexOf(player.color);
    if (paletteIndex >= 0) {
      return ENTITY_ASSETS.sheep[paletteIndex % ENTITY_ASSETS.sheep.length];
    }
  }

  const playerOrderIndex = gameState.playerOrder.indexOf(ownerId);
  if (playerOrderIndex >= 0) {
    return ENTITY_ASSETS.sheep[playerOrderIndex % ENTITY_ASSETS.sheep.length];
  }

  return ENTITY_ASSETS.sheep[0];
}

function getKingSheepAsset(ownerId: string | undefined, gameState: any): string {
  if (!ownerId) {
    return ENTITY_ASSETS.kingSheep[0];
  }

  const player = gameState.players[ownerId];
  if (player?.color) {
    const paletteIndex = PLAYER_COLORS.indexOf(player.color);
    if (paletteIndex >= 0) {
      return ENTITY_ASSETS.kingSheep[paletteIndex % ENTITY_ASSETS.kingSheep.length];
    }
  }

  const playerOrderIndex = gameState.playerOrder.indexOf(ownerId);
  if (playerOrderIndex >= 0) {
    return ENTITY_ASSETS.kingSheep[playerOrderIndex % ENTITY_ASSETS.kingSheep.length];
  }

  return ENTITY_ASSETS.kingSheep[0];
}
